import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useApp } from '../store.js';

/** First-run: create the owner account (password + TOTP enrollment). */
export function Setup() {
  const setAuth = useApp((s) => s.setAuth);
  const [step, setStep] = useState<'credentials' | 'totp'>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [secret, setSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (otpauthUrl) {
      QRCode.toDataURL(otpauthUrl, { margin: 1, width: 220 })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(''));
    }
  }, [otpauthUrl]);

  const begin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== password2) {
      setError('passwords do not match');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/auth/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error ?? 'setup failed');
        return;
      }
      setSetupToken(body.setupToken);
      setSecret(body.secret);
      setOtpauthUrl(body.otpauthUrl);
      setStep('totp');
    } catch {
      setError('network error');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/auth/setup/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setupToken, code }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error ?? 'verification failed');
        return;
      }
      setAuth('ready', body.username);
    } catch {
      setError('network error');
    } finally {
      setBusy(false);
    }
  };

  if (step === 'credentials') {
    return (
      <div className="auth-page">
        <form className="auth-card" onSubmit={begin}>
          <h1>Set up RBV Notes</h1>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Create the owner account for this vault.
          </p>
          <input
            placeholder="Username"
            value={username}
            autoCapitalize="none"
            autoCorrect="off"
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            placeholder="Password (min 10 chars)"
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <input
            placeholder="Repeat password"
            type="password"
            value={password2}
            autoComplete="new-password"
            onChange={(e) => setPassword2(e.target.value)}
          />
          {error && <div className="auth-error">{error}</div>}
          <button
            className="primary"
            disabled={busy || !username || password.length < 10 || !password2}
          >
            Continue
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={verify}>
        <h1>Two-factor setup</h1>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Scan with your authenticator app, then enter the 6-digit code.
        </p>
        {qrDataUrl && (
          <div className="qr-box">
            <img src={qrDataUrl} alt="TOTP QR code" width={220} height={220} />
          </div>
        )}
        <div className="secret-text">{secret}</div>
        <input
          placeholder="6-digit code"
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          onChange={(e) => setCode(e.target.value)}
        />
        {error && <div className="auth-error">{error}</div>}
        <button className="primary" disabled={busy || code.length < 6}>
          Verify &amp; finish
        </button>
      </form>
    </div>
  );
}
